import React, { memo, useState, forwardRef, useImperativeHandle, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { PhotoAlbum } from 'react-photo-album';
import { css } from '@emotion/core';
import _shuffle from 'lodash/shuffle';

import { breakpoints } from 'utils/breakpoint';

import MontageImg from 'ugc/components/ugcGallery/montage/MontageImg';
import { gameTypes } from 'config/promoGameTypes';

const Montage = forwardRef((_props, ref) => {
    const { type: gameType } = useSelector(s => s.promo);
    const { editor, isShowWorkInformation, setReady } = _props;

    const columnsRef = useRef(0);
    const [photos, setPhotos] = useState([]);

    const renderPhoto = useCallback(
        props => {
            const {
                photo: { linkTo, isPlaceholder, ...photoRest },
                imageProps: { style, ...rest },
                layout: { photoIndex }
            } = props;
            return (
                <MontageImg
                    css={[
                        css(style),
                        isPlaceholder && placeholderImgStyle,
                        Boolean(linkTo) && linkImgStyle
                    ]}
                    isShowPlayIcon={gameType === gameTypes.VIDEO_UPLOAD}
                    igGame={gameType === gameTypes.INSTAGRAM}
                    animationInTime={generateAnimationInTime(photoIndex + 1)}
                    isShowWorkInformation={isShowWorkInformation}
                    setReady={setReady}
                    {...rest}
                    {...photoRest}
                />
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [gameType, isShowWorkInformation]
    );

    useImperativeHandle(
        ref,
        () => ({
            setPhotos: ({ tops = [], defaults = [] } = {}) => {
                const placeholderImgCount = Math.max(
                    0,
                    columnsRef.current - (tops.length + defaults.length)
                );
                const arry = [
                    ..._shuffle(tops),
                    ..._shuffle(defaults),
                    ...getPlaceholderImgDatas(placeholderImgCount)
                ];
                setPhotos(arry);
            },
            getMediasCount: () => getMediasCount(columnsRef.current)
        }),
        []
    );

    return (
        <PhotoAlbum
            photos={photos}
            layout="masonry"
            columns={cWidth => {
                const columns = editor
                    ? getResponsiveColumnsforEditor(cWidth)
                    : getResponsiveColumns(cWidth);
                columnsRef.current = columns;
                return columns;
            }}
            spacing={spacing}
            padding={padding}
            renderPhoto={renderPhoto}
            onClick={!editor && handleGotoImg}
        />
    );
});

export default memo(Montage);

export const imgWidth = 350;
const spacing = 8;
const padding = 0;

function handleGotoImg(_e, photo) {
    const { linkTo } = photo;
    if (linkTo) {
        window.open(linkTo);
    }
}
function getPlaceholderImgDatas(quantity) {
    return Array.from(new Array(quantity), () => ({
        key: `d_${Math.random()}`,
        alt: 'default',
        width: imgWidth,
        height: generateRandomHeight(imgWidth),
        isPlaceholder: true
    }));
}
function generateRandomHeight(width) {
    const range = width * 1.6;
    return Math.max(Math.floor(Math.random() * range), 200);
}
function generateAnimationInTime(index) {
    return Math.min(2000, 500 + 100 * index);
}
function getMediasCount(columnsCount) {
    return columnsCount * 5;
}
function getResponsiveColumns(containerWidth) {
    switch (true) {
        case containerWidth <= breakpoints.sm:
            return 2;
        case containerWidth <= breakpoints.lg:
            return 3;
        case containerWidth <= breakpoints.xlg:
            return 4;
        default:
            return 5;
    }
}
function getResponsiveColumnsforEditor(containerWidth) {
    switch (true) {
        case containerWidth <= breakpoints.sm:
            return 3;
        case containerWidth <= breakpoints.lg:
            return 4;
        default:
            return 5;
    }
}

const placeholderImgStyle = css`
    cursor: auto;
`;

const linkImgStyle = css`
    cursor: pointer;
`;
